import * as React from 'react';
// import { Tooltip } from 'components';
import { PortalFieldLabel } from 'components/FormControls';
import { IMeta } from 'models/common';
import { Tooltip } from 'components';
import { translate } from 'react-i18next';
import renderIcon, { IconName } from 'helpers/renderIcon';
import useDebounce from 'helpers/debounce';
import { useEffect, useState } from 'react';
import { addURLSearchParams, deleteURLSearchParams, readURLSearchParams } from 'helpers/common';
import { withRouter, browserHistory } from 'react-router';
const classNames = require('classnames');

const INPUTFIELD_USAGE = {
	portal: '',
	admin: 'portalAdminInputField',
};

interface IProps {
	usage?: 'portal' | 'admin';
	input?: any; // dont provide input.value if you want to use debounce
	label?: any;
	type?: any;
	meta?: IMeta;
	autoFocus?: any;
	readOnly?: boolean;
	isDisabled?: boolean;
	onChangeAction?: (e: React.ChangeEvent<HTMLInputElement>) => any;
	tooltipText?: string;
	tooltipPosition?: any;
	description?: string;
	information?: string;
	className?: string;
	iconName?: IconName;
	iconClassName?: string;
	iconAction?: () => void | undefined;
	maxLength?: number;
	customRef?: React.RefObject<HTMLInputElement> | undefined;
	onFocusAction?: (e?: React.ChangeEvent<HTMLInputElement>) => void | undefined;
	onBlurAction?: (e?: React.ChangeEvent<HTMLInputElement>) => void | undefined;
	customIconSizeWidth?: number;
	customIconSizeHeight?: number;
	list?: string;
	min?: number;
	highlightWhenValueSet?: boolean;
	functionToTriggerOnSearch?: (value: string) => void;
	functionToTriggerOnSearchDelay?: number;
	putSelectedValueInUrlQuery?: string; // url prop key name
	updateFieldValueFromOutside?: boolean;
	setUrlValueInternally?: boolean; // setValue after fetching url params or not
	step?: number;
	inputMode?: string;
	customCursor?: number;
	doesNotNeedUrlChange?: boolean;

	location?: any; // obtained with withRouter
	history?: any; // obtained with withRouter

	errors?: string[];
}

const PortalInputFieldW = (props: IProps) => {
	const {
		iconName,
		iconClassName,
		iconAction,
		onBlurAction,
		onFocusAction,
		customIconSizeWidth,
		customIconSizeHeight,
		highlightWhenValueSet,
		functionToTriggerOnSearch,
		putSelectedValueInUrlQuery,
		setUrlValueInternally,
		min,
	} = props;
	const [value, setValue] = useState(!props.input?.value ? '' : props.input.value);
	const debouncedValue = useDebounce(
		value,
		props.functionToTriggerOnSearchDelay ? props.functionToTriggerOnSearchDelay : 400,
	);
	const history = browserHistory;
	//////////////////////////////////////////////
	// Update value on input.value change
	//////////////////////////////////////////////
	useEffect(() => {
		setValue(props.input?.value);
	}, [props.input?.value]);
	//////////////////////////////////////////////
	// Apply values from URL query on mount - if any
	//////////////////////////////////////////////
	useEffect(() => {
		if (putSelectedValueInUrlQuery) {
			const extractedParamValue = readURLSearchParams(putSelectedValueInUrlQuery);
			if (extractedParamValue) {
				if (props.onChangeAction) {
					props.onChangeAction(extractedParamValue as any);
				}
				if (props.input?.onChange) {
					props.input.onChange(undefined, false, extractedParamValue);
				}
				if (setUrlValueInternally) {
					setValue(extractedParamValue);
				}
			}
		}
	}, []);
	//////////////////////////////////////////////
	// Remove filters if they are removed from URL
	//////////////////////////////////////////////
	useEffect(() => {
		if (putSelectedValueInUrlQuery && !readURLSearchParams(putSelectedValueInUrlQuery)) {
			if (props.onChangeAction) {
				props.onChangeAction('' as any);
			}
			if (props.input?.onChange) {
				props.input.onChange(undefined, false, '');
			}
			setValue('');
		}
	}, [props.location]);
	//////////////////////////////////////////////
	// Debounce function before triggering it
	//////////////////////////////////////////////
	useEffect(() => {
		if (functionToTriggerOnSearch) {
			if (debouncedValue?.length && putSelectedValueInUrlQuery) {
				addURLSearchParams(putSelectedValueInUrlQuery, debouncedValue, history);
			}
			functionToTriggerOnSearch(debouncedValue);
		}
	}, [debouncedValue]);
	//////////////////////////////////////////////
	//////////////////////////////////////////////
	// Update field value from outside when passed input.value prop changes
	//////////////////////////////////////////////
	useEffect(() => {
		if (props.updateFieldValueFromOutside) {
			setValue(props.input?.value);
		}
	}, [props.updateFieldValueFromOutside, props.input?.value]);
	//////////////////////////////////////////////

	// Keep track of caret/cursor position when typing in a field
	useEffect(() => {
		if (props.customRef && props.customRef?.current && props.customCursor) {
			props.customRef.current.setSelectionRange(props.customCursor, props.customCursor);
		}
	}, [props.customRef, props.customCursor, props.input?.value]);

	let informationContainer: JSX.Element = null;
	if (typeof props.information !== 'undefined' && props.information !== null) {
		informationContainer = <div className="informationBlock">{props.information}</div>;
	}

	const usage = props.usage ? INPUTFIELD_USAGE[props.usage] : INPUTFIELD_USAGE.portal;

	let description: string | string[] = props.description || '';
	let status = '';
	if (props.meta?.touched) {
		if (props.meta?.warning) {
			status = 'withWarning';
			description = props.meta.warning;
		}
		if (props.meta?.error) {
			description = props.meta.error;
			status = 'withError';
		}
	}

	if (props.maxLength) {
		const maxLength = Number(props.maxLength);
		const value = String(props.input?.value || '');
		if (value.length > maxLength) {
			description = translate('Validation_Field_TooLong');
			status = 'withError';
		} else if (value.length <= maxLength) {
			description = [description as string, value.length + '/' + maxLength];
		}
	}

	const handleClearSelectedValue = (e: React.MouseEvent) => {
		e.stopPropagation();
		e.preventDefault();
		if (props.input?.onChange) {
			props.input.onChange(e, true);
		}
		setValue('');
		// Causes page position to reset to the top when clearing value
		if (!props?.doesNotNeedUrlChange) {
			deleteURLSearchParams([putSelectedValueInUrlQuery], history);
		}
	};

	return (
		<div className={classNames('fieldGroup', usage, status, props.className, props.readOnly ? 'readOnly' : '')}>
			{informationContainer}
			<label
				className={`inputHolder cursor-text-hover ${highlightWhenValueSet && props.input?.value ? 'highlight' : ''}`}
			>
				<input
					{...props.input}
					className={classNames('inputField', {
						value: props.input?.value,
						hasIconRight: iconName,
						withoutLabel: !props.label,
					})}
					type={props.type}
					autoFocus={props.autoFocus}
					readOnly={props.readOnly}
					disabled={props.isDisabled}
					ref={props.customRef}
					list={props.list}
					value={value}
					min={min}
					step={props.step}
					inputMode={props.inputMode}
					onChange={(e) => {
						////////////////////////////////////////////////////
						// Add values as URL search params
						////////////////////////////////////////////////////
						if (putSelectedValueInUrlQuery && !e.target.value) {
							deleteURLSearchParams([putSelectedValueInUrlQuery], history);
						}
						////////////////////////////////////////////////////
						if (props.onChangeAction) {
							props.onChangeAction(e);
						}
						if (props.input?.onChange) {
							props.input.onChange(e);
						}
						setValue(e.target.value);
					}}
					onBlur={onBlurAction}
					onFocus={onFocusAction}
				/>
				{iconName
					? value
						? renderIcon(
								'cancel_search',
								customIconSizeWidth || undefined,
								customIconSizeWidth || undefined,
								'clearFieldIcon pointer',
								handleClearSelectedValue, // Needs a rework
						  )
						: renderIcon(iconName, customIconSizeWidth || 22, customIconSizeHeight || 22, iconClassName, iconAction)
					: null}
				<PortalFieldLabel name={props.input.name} text={props.label} tag="div" />
				{props.tooltipText && props.tooltipText.length > 0 ? (
					<Tooltip position={props.tooltipPosition} message={props.tooltipText}>
						<a href="#" onClick={(e) => e.preventDefault()}>
							<img src={require('containers/Html/icons/tooltip-icon.png')} />
						</a>
					</Tooltip>
				) : null}
			</label>
			<div className="inputFieldErrors">
				{props.errors ? (
					props.errors.map((err, index) => (
						<span key={index} className="note">
							{err}
						</span>
					))
				) : description ? (
					<span className={'note' + (Array.isArray(description) ? ' split' : '')}>
						{Array.isArray(description) ? description.map((d, i) => <span key={i}>{d}</span>) : description}
					</span>
				) : null}
			</div>
		</div>
	);
};

export const PortalInputField = withRouter(PortalInputFieldW);
